(function () {
    window.NewsGraph = window.NewsGraph || {};

    class NewsGraphBuilder {
        static setupEnvironmentMap(sceneEl) {
            const THREE = window.THREE;
            const renderer = sceneEl.renderer;
            
            const pmremGenerator = new THREE.PMREMGenerator(renderer);
            pmremGenerator.compileEquirectangularShader();

            const envScene = new THREE.Scene();

            const bgGeo = new THREE.SphereGeometry(50, 16, 16);
            const bgMat = new THREE.MeshBasicMaterial({ color: 0x1a1a2e, side: THREE.BackSide });
            envScene.add(new THREE.Mesh(bgGeo, bgMat));

            const panelGeo = new THREE.PlaneGeometry(25, 25);
            const keyPanel = new THREE.Mesh(panelGeo, new THREE.MeshBasicMaterial({ color: 0xffffff }));
            keyPanel.position.set(20, 20, 15);
            keyPanel.lookAt(0, 0, 0);
            envScene.add(keyPanel);

            const fillPanel = new THREE.Mesh(panelGeo, new THREE.MeshBasicMaterial({ color: 0x99aacc }));
            fillPanel.position.set(-20, -10, 15);
            fillPanel.lookAt(0, 0, 0);
            envScene.add(fillPanel);

            const rimPanel = new THREE.Mesh(panelGeo, new THREE.MeshBasicMaterial({ color: 0x667799 }));
            rimPanel.position.set(0, 15, -20);
            rimPanel.lookAt(0, 0, 0);
            envScene.add(rimPanel);

            const bottomPanel = new THREE.Mesh(panelGeo, new THREE.MeshBasicMaterial({ color: 0x333344 }));
            bottomPanel.position.set(0, -25, 0);
            bottomPanel.lookAt(0, 0, 0);
            envScene.add(bottomPanel);

            const envMap = pmremGenerator.fromScene(envScene, 0, 0.1, 100).texture;
            pmremGenerator.dispose();
            
            return envMap;
        }

        static buildNodes(nodes, pivotEl, isVR = false) {
            const counts = nodes.map(n => n.article_count || 0);
            const minC   = Math.min(...counts);
            const maxC   = Math.max(...counts);

            const meshMap   = {};
            const nodeEls   = [];
            const coneInfos = [];

            const narrativeNodes = nodes.filter(n => window.NewsGraph.NARRATIVE_TYPES.has(n.type));
            const tickerNodes    = nodes.filter(n => !window.NewsGraph.NARRATIVE_TYPES.has(n.type));

            let narrativeIdx = 0;
            let tickerIdx    = 0;

            const intensity = isVR ? 1.7 : 1.15;

            nodes.forEach(node => {
                const isSphere = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
                const baseRadius = window.NewsGraph.nodeRadius(node.article_count || 0, minC, maxC);
                const r = baseRadius * (isSphere ? 2.0 : 1.0);

                const el = document.createElement('a-entity');

                const baseColorHex = window.NewsGraph.getColor(node.direction);
                const colorStr = '#' + baseColorHex.toString(16).padStart(6, '0');

                let theta, phi, dist;
                if (isSphere) {
                    const total = Math.max(narrativeNodes.length, 1);
                    const i     = narrativeIdx;
                    phi   = Math.acos(1 - 2 * (i + 0.5) / total);
                    theta = Math.PI * (1 + Math.sqrt(5)) * i; // golden angle
                    dist  = 1.0;
                    narrativeIdx++;
                } else {
                    const total = Math.max(tickerNodes.length, 1);
                    const i     = tickerIdx;
                    phi   = Math.acos(1 - 2 * (i + 0.5) / total);
                    theta = Math.PI * (1 + Math.sqrt(5)) * i; // golden angle
                    dist  = 5.0;
                    tickerIdx++;
                }

                el.object3D.position.set(
                    dist * Math.sin(phi) * Math.cos(theta),
                    dist * Math.sin(phi) * Math.sin(theta),
                    dist * Math.cos(phi)
                );

                pivotEl.appendChild(el);

                if (isSphere) {
                    el.setAttribute('geometry', `primitive: sphere; radius: ${r}; segmentsWidth: 20; segmentsHeight: 14`);
                } else {
                    el.setAttribute('geometry', `primitive: cone; radiusBottom: ${r}; radiusTop: 0; height: ${r * 2}; segmentsRadial: 24; segmentsHeight: 1`);
                }
                const isNeutral = !node.direction || node.direction === 'neutral';
                const isFresh = node.freshness && (node.freshness.status === 'inflection' || node.freshness.status === 'new');
                const materialConfig = isNeutral
                    ? {
                        color: '#ccccdd',
                        metalness: isFresh ? 0.0 : 0.7,
                        roughness: isFresh ? 0.5 : 0.3,
                        transparent: true,
                        opacity: 0.82,
                        side: 'double',
                        blending: 'normal',
                        depthTest: true,
                        depthWrite: true,
                        emissive: isFresh ? '#ccccdd' : '#ccccdd',
                        emissiveIntensity: isFresh ? 0.05 : 0.2
                    }
                    : {
                        color: colorStr,
                        transparent: true,
                        opacity: 0.65,
                        side: 'double',
                        blending: 'normal',
                        depthTest: true,
                        depthWrite: true,
                        emissive: isFresh ? colorStr : '#000000',
                        emissiveIntensity: isFresh ? 0.05 : 0.0
                    };
                el.setAttribute('material', materialConfig);

                el.userData = { radius: r };
                if (isFresh) {
                    el.userData.isFresh = true;
                    el.userData.emissiveMin = 0.05;
                    el.userData.emissiveMax = 0.6;

                    const spriteMat = new THREE.SpriteMaterial({
                        map: window.NewsGraph.sharedGlowTexture = window.NewsGraph.sharedGlowTexture || window.NewsGraph.createGlowTexture(),
                        color: isNeutral ? '#ccccdd' : colorStr,
                        transparent: true,
                        opacity: 0.0,
                        blending: THREE.AdditiveBlending,
                        depthWrite: false
                    });
                    const sprite = new THREE.Sprite(spriteMat);
                    sprite.raycast = function() {};
                    el.userData.baseGlowScale = r * 2.5;
                    sprite.scale.set(el.userData.baseGlowScale, el.userData.baseGlowScale, 1);
                    el.setObject3D('glowSprite', sprite);
                }

                if (!isSphere) {
                    coneInfos.push({ el, node, flipped: false });
                }

                meshMap[node.id] = el;
                nodeEls.push(el);
            });
            
            return { meshMap, nodeEls, coneInfos };
        }

        static buildEdges(links, meshMap, pivot, isVR = false) {
            const THREE = window.THREE;
            const group = new THREE.Group();
            pivot.add(group);

            links.forEach(link => {
                const mA = meshMap[link.source];
                const mB = meshMap[link.target];
                if (!mA || !mB) return;

                const p1 = mA.object3D.position.clone();
                const p2 = mB.object3D.position.clone();

                const thickness = 0.03 + (link.strength / 100) * 0.09;
                const opacity   = 0.35 + (link.strength / 100) * 0.40;

                const geo  = new THREE.TubeGeometry(new THREE.LineCurve3(p1, p2), 1, thickness, 6, false);
                const mat  = new THREE.MeshBasicMaterial({
                    color: 0x94a3b8,
                    transparent: true,
                    opacity,
                });
                group.add(new THREE.Mesh(geo, mat));
            });

            return group;
        }

        static buildLabels(nodes, labelRoot) {
            const labelMap = {};

            nodes.forEach(node => {
                const div = document.createElement('div');
                div.style.cssText = [
                    'position:absolute',
                    'color:var(--color-text-primary,#e2e8f0)',
                    'font-size:10px',
                    'line-height:1.3',
                    'font-weight:500',
                    'white-space:nowrap',
                    'text-shadow:0 1px 3px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,1)',
                    'pointer-events:none',
                    'transform:translate(-50%,-50%)',
                    'max-width:160px',
                    'overflow:hidden',
                    'text-overflow:ellipsis',
                    'font-family:inherit',
                    'letter-spacing:0.01em',
                ].join(';');

                let raw = node.label || node.id;
                if (node.type === 'ticker' || node.type === 'ticker_group') {
                    raw = raw.split(' - ')[0].trim();
                }
                div.textContent = raw.length > 32 ? raw.slice(0, 32) + '…' : raw;
                div.title = node.label || node.id;

                labelRoot.appendChild(div);
                labelMap[node.id] = div;
            });
            
            return labelMap;
        }
    }

    window.NewsGraph.NewsGraphBuilder = NewsGraphBuilder;
})();
